import React, { useState } from "react";
import { Link } from "react-router-dom";
import { apiRequest } from "../utils/api";
import CaptchaBox from "./Common/CaptchaBox";
import FormFeedback, { FieldError, inputErrorClass } from "./Common/FormFeedback";
import Seo from "./Common/Seo";

const contactBlocks = [
  {
    id: "phone",
    icon: "/images/icons/smartphone.svg",
    iconAlt: "phone",
    title: "Call Us",
    content: "832.795.3431",
    href: "tel:8327953431",
  },
  {
    id: "email",
    icon: "/images/icons/letter.svg",
    iconAlt: "email",
    title: "Email",
    content: "Support@Epic-Jobs.com",
    href: "mailto:Support@Epic-Jobs.com",
  },
  {
    id: "support",
    icon: "/images/icons/placeholder.svg",
    iconAlt: "support",
    title: "Support",
    content: "Questions about job posts, applications, accounts, or billing can be submitted here.",
  },
];

const contactFields = [
  { id: "name", name: "name", label: "Your Name", type: "text", placeholder: "Your Name*", className: "username", columns: "col-lg-6 col-md-12 col-sm-12" },
  { id: "email", name: "email", label: "Your Email", type: "email", placeholder: "Your Email*", className: "email", columns: "col-lg-6 col-md-12 col-sm-12" },
  { id: "subject", name: "subject", label: "Subject", type: "text", placeholder: "Subject *", className: "subject", columns: "col-lg-12 col-md-12 col-sm-12" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [isSending, setIsSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [captcha, setCaptcha] = useState({ id: "", answer: "" });
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((currentForm) => ({ ...currentForm, [name]: value }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    setSuccess(false);
    setError(false);
    setValidationErrors([]);
    setFieldErrors({});
    setIsSending(true);

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => formData.append(key, value));
    formData.append("captcha_id", captcha.id);
    formData.append("captcha_answer", captcha.answer);

    apiRequest("/contact-messages", { method: "POST", body: formData })
      .then((data) => {
        setSuccess(data.message || "Message submitted successfully.");
        setForm({ name: "", email: "", subject: "", message: "", website: "" });
        setCaptchaResetKey((current) => current + 1);
      })
      .catch((error) => {
        if (error.data?.status === "validation_error") {
          setValidationErrors(error.messages);
          setFieldErrors(error.errors);
          setCaptchaResetKey((current) => current + 1);
        }
        else {
          setError(error.message);
          setCaptchaResetKey((current) => current + 1);
        }
      })
      .finally(() => setIsSending(false));
  }

  return (
    <>
      <Seo title="Contact Epic-Jobs" description="Contact Epic-Jobs support for job posts, applications, employer accounts, candidate profiles, billing, and platform questions." />
      <section className="contact-section">
        <div className="auto-container">
          <div className="upper-box">
            <div className="row">
              {contactBlocks.map((item) => (
                <div className="contact-block col-lg-4 col-md-6 col-sm-12" key={item.id}>
                  <div className="inner-box">
                    <span className="icon"><img src={item.icon} alt={item.iconAlt} /></span>
                    <h4>{item.title}</h4>
                    <p>{item.href ? <a href={item.href}>{item.content}</a> : item.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="contact-form default-form">
            <h3>Leave A Message</h3>
            <form onSubmit={handleSubmit} id="email-form">
              <div className="row">
                <div className="form-group col-lg-12 col-md-12 col-sm-12">
                  <FormFeedback success={success} error={error} validationErrors={validationErrors} />
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    className="contact-honeypot"
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                  />
                </div>

                {contactFields.map((field) => (
                  <div className={`${field.columns} form-group`} key={field.id}>
                    <label>{field.label}</label>
                    <input
                      type={field.type}
                      name={field.name}
                      className={`${field.className} ${inputErrorClass(fieldErrors, field.name)}`}
                      placeholder={field.placeholder}
                      required
                      value={form[field.name]}
                      onChange={handleChange}
                    />
                    <FieldError errors={fieldErrors} name={field.name} />
                  </div>
                ))}

                <div className="col-lg-12 col-md-12 col-sm-12 form-group">
                  <label>Your Message</label>
                  <textarea name="message" className={inputErrorClass(fieldErrors, "message")} placeholder="Write your message..." required value={form.message} onChange={handleChange}></textarea>
                  <FieldError errors={fieldErrors} name="message" />
                </div>

                <div className="col-lg-12 col-md-12 col-sm-12 form-group">
                  <CaptchaBox
                    value={captcha}
                    onChange={setCaptcha}
                    errors={fieldErrors}
                    resetKey={captchaResetKey}
                  />
                </div>

                <div className="col-lg-12 col-md-12 col-sm-12 form-group">
                  <button className="theme-btn btn-style-one" type="submit" id="submit" name="submit-form" disabled={isSending}>
                    {isSending ? "Sending..." : "Send Message"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="call-to-action style-two">
        <div className="auto-container">
          <div className="outer-box">
            <div className="content-column">
              <div className="sec-title">
                <h2>Recruiting?</h2>
                <div className="text">Advertise jobs, review applicants, and manage posting access from one employer dashboard.</div>
                <Link to="/pricing" className="theme-btn btn-style-one bg-blue"><span className="btn-title">Start Recruiting Now</span></Link>
              </div>
            </div>

            <div className="image-column" style={{ backgroundImage: "url(/images/resource/image-1.png)" }}>
              <figure className="image"><img src="/images/resource/image-1.png" alt="cta" /></figure>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
